import React from 'react';
import { Controller } from 'react-hook-form';
import * as yup from 'yup';

import Button from '@/core/components/Button';
import HintMessage from '@/core/components/HintMessage';
import CodeInput from '@/core/components/inputs/CodeInput';
import Label from '@/core/components/Label';
import useForm from '@/core/hooks/useForm';
import { useEnvContext } from '@/core/lib/env/env.context';
import { ApiMethods } from '@/core/lib/fetch';
import { ApiVersions } from '@/core/lib/fetch/fetch';
import Query from '@/core/lib/new-architecture/query';
import { OTP_REGEX } from '@/core/lib/regex';
import { useTranslationContext } from '@/core/lib/translation/translation.context';
import type { UsersHasControlResult } from '@/core/types/control';

const schema = yup.object({
  otp: yup.string().matches(OTP_REGEX, 'control.email-check.6-digits').required('control.email-check.required'),
});

type Schema = yup.InferType<typeof schema>;

const EmailErrorControlCode: React.FC<{ newMail: string | undefined }> = ({ newMail }) => {
  const { t } = useTranslationContext(['common']);
  const ns = { ns: 'common' };
  const { DONNONS_API_URL } = useEnvContext();
  const { invalidate } = Query.user.useInvalidate();

  const { control, onSubmit, errors } = useForm<UsersHasControlResult, Schema>({
    schema,
    apiDefinition: { method: ApiMethods.PUT, url: `${DONNONS_API_URL}${ApiVersions.V1}/user/email` },
    onSuccess: async () => {
      invalidate();
    },
  });

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex w-full grow flex-col gap-6">
        <div className="mx-auto flex size-30 items-center justify-center">
          <img loading="lazy" decoding="async" src="/assets/control/email_check.svg" alt={t('control.rs.title', { ns: 'common' })} className="object-contain" />
        </div>
        <p className="text-body-primary text-center font-semibold text-content-secondary">{t('control.email-error.code-confirmation.title', ns)}</p>
        {newMail && (
          <p className="text-body-secondary font-normal text-content-primary">
            {t('control.email-error.code-confirmation.subtitle', ns)}
            <span className="font-medium">{newMail}</span>
          </p>
        )}
        <Controller
          control={control}
          defaultValue=""
          render={({ field: { onChange, value } }) => {
            const { isError, message, isSuccess } = errors.otp;
            return (
              <Label htmlFor="otp" content={t('control.email-error.code-confirmation.input-label', ns)}>
                <CodeInput id="otp" onChange={onChange} value={value} />
                <HintMessage isError={isError} isSuccess={isSuccess} message={message} />

                <p className="text-caption-primary mt-3 text-center font-normal text-content-primary">{t('control.email-error.code-confirmation.input-helper', ns)}</p>
              </Label>
            );
          }}
          name="otp"
        />
      </div>
      <div className="mt-auto flex w-full justify-end">
        <div className="max-w-max">
          <Button variant="primary" onClick={onSubmit}>
            {t('control.email-error.code-confirmation.cta-submit', ns)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmailErrorControlCode;
